import { ColorScheme, DEFAULT_CARD_BACKGROUND } from "../constants/colorSchemes";
import {
  colorsMatch,
  contrastingTextColor,
  hasSufficientContrast,
} from "./colorUtils";

type ThemeColorFields = {
  originalTextColor?: string;
  textColor?: string;
  originalDisplayTextColor?: string;
  displayTextColor?: string;
  originalBackgroundColor?: string;
  backgroundColor?: string;
  displayBackgroundColor?: string;
  cardBackgroundColor?: string;
};

export const APP_PAGE_TEXT_CLASS = "text-theme-text";

export const getBackgroundColor = (theme: ThemeColorFields): string =>
  theme.originalBackgroundColor || theme.backgroundColor || "#F8F9FA";

/** App UI text color — derived from the active color scheme / background. */
export const getAppTextColor = (theme: ThemeColorFields): string =>
  theme.originalTextColor ||
  theme.textColor ||
  contrastingTextColor(getBackgroundColor(theme));

export const getDisplayBackgroundColor = (theme: ThemeColorFields): string =>
  theme.displayBackgroundColor || getBackgroundColor(theme);

/** Default full-screen text when the page theme changes. */
export const getDefaultDisplayTextColor = (backgroundColor: string): string =>
  contrastingTextColor(backgroundColor);

export const getStoredDisplayTextColor = (theme: ThemeColorFields): string =>
  theme.originalDisplayTextColor ||
  theme.displayTextColor ||
  getDefaultDisplayTextColor(getDisplayBackgroundColor(theme));

/** If a saved display color would clash, fall back to black or white. */
export const getReadableDisplayTextColor = (theme: ThemeColorFields): string => {
  const background = getDisplayBackgroundColor(theme);
  const stored = getStoredDisplayTextColor(theme);
  if (hasSufficientContrast(stored, background)) {
    return stored;
  }
  return getDefaultDisplayTextColor(background);
};

export const getThemeColors = (theme: ThemeColorFields) => {
  const pageBg = getBackgroundColor(theme);
  const cardBg = theme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND;

  return {
    appText: getAppTextColor(theme),
    displayText: getReadableDisplayTextColor(theme),
    pageBg,
    cardBg,
    displayBg: getDisplayBackgroundColor(theme),
  };
};

export const isDisplayTextChoiceDisabled = (
  textColor: string,
  displayBackground: string,
): boolean => !hasSufficientContrast(textColor, displayBackground);

export const isCardBackgroundChoiceDisabled = (
  cardColor: string,
  pageBackground: string,
): boolean => colorsMatch(cardColor, pageBackground);

export const applyColorSchemeColors = (scheme: ColorScheme) => {
  const appTextColor = scheme.textColor;
  const defaultDisplayTextColor = getDefaultDisplayTextColor(
    scheme.backgroundColor,
  );

  return {
    ...scheme,
    originalTextColor: appTextColor,
    textColor: appTextColor,
    originalDisplayTextColor: defaultDisplayTextColor,
    displayTextColor: defaultDisplayTextColor,
    originalBackgroundColor: scheme.backgroundColor,
    displayBackgroundColor: scheme.backgroundColor,
  };
};
